import { Grid } from '@mui/joy';
import { SxProps } from '@mui/material';
import { upperFirst } from 'lodash';
import { UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { AllowedDecimalsTooltip, LabeledValue } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { PricingHelper } from 'pricing/helpers';
import { useCostDecimals } from 'pricing/hooks';
import { ICalculatePnbPriceForm } from 'quotes/types';
import { Level } from 'theme/enums';

import { getLabelByType } from './utils';

interface IProps {
  cost: number;
  onPriceChange: (
    price: number,
    cost: number,
    setValue: UseFormSetValue<ICalculatePnbPriceForm>,
    type?: string,
    priceBase?: number,
    priceAdder?: number
  ) => void;
  decimals: number;
  type: string;
  containerStyle?: SxProps;
  disabled?: boolean;
}

export const PricingGroup = ({
  cost,
  onPriceChange,
  decimals,
  type,
  containerStyle = {},
  disabled = false,
}: IProps) => {
  const { t } = useTranslation();
  const costDecimals = useCostDecimals();

  const { control, errors, setValue, watch } = useFormContext();

  const labelType = getLabelByType(type);

  return (
    <Grid container key={`${type} cost - ${cost}`} spacing={2} sx={containerStyle}>
      <Grid xs={12} md={6}>
        <LabeledValue
          label={t(`label${labelType}StandardCost`)}
          value={PricingHelper.formatPricing({
            amount: cost,
            decimals: costDecimals,
            fixedDecimals: true,
          })}
          options={{
            hasColon: false,
            labelLevel: Level.BODY_SM,
            valueLevel: Level.TITLE_LG,
            labelSx: { marginBottom: '8px' },
          }}
        />
      </Grid>
      <Grid xs={12} md={6}>
        <FormField
          name={`price${upperFirst(type)}`}
          type={InputType.NUMBER_INPUT}
          inputProps={{
            label: t(`label${labelType}Price`),
            placeholder: t(`label${labelType}Price`),
            control: control,
            error: errors[`price${upperFirst(type)}`],
            onChange: ({ target: { value }, source }) => {
              if (source !== 'event') {
                return;
              }

              onPriceChange(
                Number(value),
                cost,
                setValue,
                type,
                watch('priceBase'),
                watch('priceAdder')
              );
            },
            disabled,
            endDecorator: <AllowedDecimalsTooltip decimals={decimals} />,
            slotProps: {
              input: {
                thousandSeparator: true,
                decimalScale: decimals,
                prefix: '$',
              },
            },
          }}
        />
      </Grid>
    </Grid>
  );
};
